// Supplier dashboard with multi-tab navigation and task management

import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAuth, useRoleCheck } from '../../hooks/useAuth'
import { useSupplierTasks, useSupplierEvents } from '../../hooks/useSupplierTasks'
import { TaskCard } from './TaskCard'
import { TaskAcknowledgmentModal } from './TaskAcknowledgmentModal'
import { LocationView } from './LocationView'
import { ComplianceView } from './ComplianceView'
import { EventSelector } from './EventSelector'
import { CountdownTimer } from './CountdownTimer'
import { getAppConfig } from '../../types/env'
import styles from './SupplierDashboard.module.css'

type DashboardTab = 'schedule' | 'location' | 'compliance'

interface SupplierDashboardProps {
  defaultEventId?: string
}

export const SupplierDashboard: React.FC<SupplierDashboardProps> = ({
  defaultEventId
}) => {
  const { eventId: urlEventId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isSupplier } = useRoleCheck()
  const config = getAppConfig()

  const [activeTab, setActiveTab] = useState<DashboardTab>('schedule')
  const [selectedEventId, setSelectedEventId] = useState<string>(
    urlEventId || defaultEventId || ''
  )
  const [selectedTaskForAck, setSelectedTaskForAck] = useState<string | null>(null)
  const [showEventSelector, setShowEventSelector] = useState(false)

  const { data: events, isLoading: eventsLoading } = useSupplierEvents()
  const { 
    data: tasks, 
    isLoading: tasksLoading, 
    refetch: refetchTasks,
    acknowledgeTask
  } = useSupplierTasks(selectedEventId)

  // Redirect non-suppliers
  useEffect(() => {
    if (!isSupplier) {
      navigate('/unauthorized')
    }
  }, [isSupplier, navigate])

  // Set default event if none selected and events are loaded
  useEffect(() => {
    if (!selectedEventId && events && events.length > 0 && !eventsLoading) {
      setSelectedEventId(events[0].id)
    }
  }, [selectedEventId, events, eventsLoading])

  // Update URL when event changes
  useEffect(() => {
    if (selectedEventId && selectedEventId !== urlEventId) {
      navigate(`/supplier/${selectedEventId}`, { replace: true })
    }
  }, [selectedEventId, urlEventId, navigate])

  const handleEventChange = (newEventId: string) => {
    setSelectedEventId(newEventId)
    setActiveTab('schedule') // Reset to schedule tab when changing events
    setShowEventSelector(false)
  }

  const handleTaskAcknowledge = async (taskId: string, notes?: string) => {
    try {
      await acknowledgeTask(taskId, notes)
      setSelectedTaskForAck(null)
      // Refresh tasks to get updated acknowledgment status
      refetchTasks()
    } catch (error) {
      console.error('Failed to acknowledge task:', error)
      // Error will be shown in the modal
    }
  }

  const currentEvent = events?.find(event => event.id === selectedEventId)
  const upcomingTasks = tasks?.filter(task => 
    new Date(task.startTime) > new Date() && !task.acknowledged
  ) || []
  const nextTask = upcomingTasks.sort((a, b) => 
    new Date(a.startTime).getTime() - new Date(b.startTime).getTime()
  )[0]

  if (eventsLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading your events...</p>
      </div>
    )
  }

  if (!events || events.length === 0) {
    return (
      <div className={styles.emptyState}>
        <div className={styles.emptyIcon}>📅</div>
        <h2>No Events Assigned</h2>
        <p>You don't have any events assigned to you yet.</p>
        <p>Contact your event coordinator to get started.</p>
      </div>
    )
  }

  if (!selectedEventId) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Setting up your dashboard...</p>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      {/* Header with event selector and countdown */}
      <div className={styles.header}>
        <div className={styles.eventInfo}>
          <button
            className={styles.eventSelector}
            onClick={() => setShowEventSelector(!showEventSelector)}
            aria-label="Select event"
          >
            <h1 className={styles.eventTitle}>
              {currentEvent?.name || 'Select Event'}
            </h1>
            <div className={styles.eventMeta}>
              {currentEvent?.date && (
                <span className={styles.eventDate}>{currentEvent.date}</span>
              )}
              {currentEvent?.venue && (
                <span className={styles.eventVenue}>{currentEvent.venue}</span>
              )}
            </div>
            <div className={styles.dropdownIcon}>▼</div>
          </button>

          {showEventSelector && events.length > 1 && (
            <EventSelector
              events={events}
              selectedEventId={selectedEventId}
              onEventChange={handleEventChange}
              onClose={() => setShowEventSelector(false)}
            />
          )}
        </div>

        {nextTask && (
          <div className={styles.countdownContainer}>
            <CountdownTimer
              targetTime={nextTask.startTime}
              taskName={nextTask.title}
              variant="compact"
            />
          </div>
        )}
      </div>

      {/* Tab navigation */}
      <div className={styles.tabContainer}>
        <div className={styles.tabs}>
          <button
            className={`${styles.tab} ${activeTab === 'schedule' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('schedule')}
          >
            <span className={styles.tabIcon}>📋</span>
            Schedule
            {upcomingTasks.length > 0 && (
              <span className={styles.badge}>{upcomingTasks.length}</span>
            )}
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'location' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('location')}
          >
            <span className={styles.tabIcon}>📍</span>
            Location
          </button>
          <button
            className={`${styles.tab} ${activeTab === 'compliance' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('compliance')}
          >
            <span className={styles.tabIcon}>📄</span>
            Compliance
          </button>
        </div>

        {/* Quick actions */}
        <div className={styles.quickActions}>
          <button
            className={styles.refreshButton}
            onClick={() => refetchTasks()}
            title="Refresh tasks"
            disabled={tasksLoading}
          >
            🔄
          </button>
        </div>
      </div>

      {/* Tab content */}
      <div className={styles.content}>
        {activeTab === 'schedule' && (
          <div className={styles.scheduleView}>
            {tasksLoading ? (
              <div className={styles.loadingContainer}>
                <div className={styles.spinner}></div>
                <p>Loading your tasks...</p>
              </div>
            ) : tasks && tasks.length > 0 ? (
              <>
                {/* Summary stats */}
                <div className={styles.summaryStats}>
                  <div className={styles.stat}>
                    <div className={styles.statValue}>{tasks.length}</div>
                    <div className={styles.statLabel}>Total Tasks</div>
                  </div>
                  <div className={styles.stat}>
                    <div className={styles.statValue}>
                      {tasks.filter(t => t.acknowledged).length}
                    </div>
                    <div className={styles.statLabel}>Acknowledged</div>
                  </div>
                  <div className={styles.stat}>
                    <div className={styles.statValue}>{upcomingTasks.length}</div>
                    <div className={styles.statLabel}>Upcoming</div>
                  </div>
                </div>

                {/* Task list */}
                <div className={styles.taskList}>
                  {tasks
                    .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
                    .map(task => (
                      <TaskCard
                        key={task.id}
                        task={task}
                        onAcknowledge={() => setSelectedTaskForAck(task.id)}
                        showCountdown={!task.acknowledged && new Date(task.startTime) > new Date()}
                      />
                    ))
                  }
                </div>
              </>
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.emptyIcon}>📋</div>
                <h3>No Tasks Assigned</h3>
                <p>You don't have any tasks for this event yet.</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'location' && (
          <LocationView eventId={selectedEventId} />
        )}

        {activeTab === 'compliance' && (
          <ComplianceView eventId={selectedEventId} />
        )}
      </div>

      {/* Task acknowledgment modal */}
      {selectedTaskForAck && (
        <TaskAcknowledgmentModal
          task={tasks?.find(t => t.id === selectedTaskForAck)}
          onAcknowledge={(notes) => handleTaskAcknowledge(selectedTaskForAck, notes)}
          onCancel={() => setSelectedTaskForAck(null)}
        />
      )}

      {/* Debug info in development */}
      {config.debug.auth && (
        <div className={styles.debugInfo}>
          <h4>Debug Info</h4>
          <pre>{JSON.stringify({ 
            user: { id: user?.id, role: user?.role, supplier_id: user?.supplier_id },
            selectedEventId,
            eventsCount: events?.length,
            tasksCount: tasks?.length,
            activeTab 
          }, null, 2)}</pre>
        </div>
      )}
    </div>
  )
}